'use client';

import { useEffect, useRef } from 'react';
import { firePurchaseStatusEvent } from './purchaseTracking';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import { feApiFrontClient } from '$/(cms)/_modules/feApiClient/feApiFrontClient';
import { useClientContext } from '$/modules/context/client';
import type { HomeInsurancePersistedPurchase } from '$/types/homeInsurance';
import type { PolicySummaryPurchase } from '$/types/policySummary';
import type {
  PolicySummaryResponse,
  PolicySummaryState,
} from '$/types/travelInsurance';

// localStorage key written by the TI checkout flow before redirecting to
// payment (and read by the current HubSpot success page). Kept as a local
// constant so the (cms) page does not import from the (application) checkout;
// this is a stable persisted contract.
export const PURCHASE_CONTEXT_STORAGE_KEY = 'successful-ti-purchases';

// localStorage key written by the HI checkout flow
// (persistHomeInsurancePurchaseContext) before redirecting to payment. Kept
// local for the same reason as PURCHASE_CONTEXT_STORAGE_KEY above: the (cms)
// page must not import from the (application) checkout.
export const HI_PURCHASE_CONTEXT_STORAGE_KEY = 'successful-hi-purchases';

type PurchaseSnapshotBase = {
  applicationId: string;
  firstName?: string;
};

export type TravelPurchaseSnapshot = PurchaseSnapshotBase & {
  vertical: 'TI';
  // Persisted by the TI checkout (persistPurchaseContext) alongside firstName;
  // consumed by the GA `purchase_status` event (CMS-1794 parity with HubSpot).
  marketingConsent?: string;
  searchResultParameters?: string;
  userData?: Record<string, unknown>;
  userIdentities?: Record<string, unknown>;
};

// What persistHomeInsurancePurchaseContext writes, read back from
// localStorage - every field optional because the entry may predate a field.
export type HomePurchaseSnapshot = PurchaseSnapshotBase & {
  vertical: 'HI';
} & Partial<
    Omit<HomeInsurancePersistedPurchase, 'applicationId' | 'firstName'>
  >;

export type PurchaseSnapshot = TravelPurchaseSnapshot | HomePurchaseSnapshot;

/**
 * Read the `application-id` query param identifying the purchase (client-only;
 * the param must not be read on the server - it would break SSG/ISR caching).
 * Owned here alongside readPurchaseSnapshot so every confirmation-page reader
 * shares the one param name.
 */
export const getApplicationIdFromUrl = (): string | null =>
  typeof window === 'undefined'
    ? null
    : new URLSearchParams(window.location.search).get('application-id');

/** Find the local purchase snapshot for the given application id (client-only). */
export const readPurchaseSnapshot = (
  applicationId: string | null,
): PurchaseSnapshot | undefined => {
  if (!applicationId || typeof window === 'undefined') {
    return undefined;
  }

  // Each key is read in its own try/catch: a corrupt value under one key
  // (TI or HI) must not block reading the other.
  try {
    const raw = window.localStorage.getItem(PURCHASE_CONTEXT_STORAGE_KEY);
    if (raw) {
      const list = JSON.parse(raw) as Omit<
        TravelPurchaseSnapshot,
        'vertical'
      >[];
      const match = Array.isArray(list)
        ? list.find((entry) => entry?.applicationId === applicationId)
        : undefined;
      if (match) {
        return { ...match, vertical: 'TI' };
      }
    }
  } catch {
    // fall through and try the HI key below
  }

  try {
    const raw = window.localStorage.getItem(HI_PURCHASE_CONTEXT_STORAGE_KEY);
    if (raw) {
      const list = JSON.parse(raw) as Omit<HomePurchaseSnapshot, 'vertical'>[];
      const match = Array.isArray(list)
        ? list.find((entry) => entry?.applicationId === applicationId)
        : undefined;
      if (match) {
        return { ...match, vertical: 'HI' };
      }
    }
  } catch {
    return undefined;
  }

  return undefined;
};

/**
 * Compute the confirmation-page state from the three inputs. Validity requires
 * BOTH the API record AND the local purchase snapshot (anti-enumeration; parity
 * with the HubSpot success page). Missing either -> the error/invalid state.
 */
export const resolvePolicySummaryState = (
  applicationId: string | null,
  snapshot: PurchaseSnapshot | undefined,
  data: PolicySummaryResponse | undefined,
): PolicySummaryState => {
  if (!applicationId || !snapshot || !data) {
    return { status: 'invalid', isPending: false };
  }

  // The policy-summary API response carries no vertical discriminator (same
  // shape is fetched for both TI and HI) - stamp it from the local snapshot,
  // and for HI carry over the snapshot-only fields the API never returns.
  const purchase: PolicySummaryPurchase =
    snapshot.vertical === 'HI'
      ? {
          ...data,
          vertical: 'HI',
          grossArea: snapshot.grossArea,
          residenceType: snapshot.residenceType,
          productUid: snapshot.productUid,
        }
      : { ...data, vertical: 'TI' };

  return {
    status: 'ready',
    // Manual underwriting = "application under review" pending copy (CMS-1794).
    isPending: Boolean(data.isManualWriting),
    firstName: snapshot.firstName,
    data: purchase,
  };
};

/**
 * One-shot resolver for the TI purchase-success page (CMS-1794): reads the
 * `application-id` query param + the local purchase snapshot, fetches the
 * policy summary via the fe API route, and publishes the resolved state into
 * the page context slice for the confirmation blocks to read. All client-side
 * after hydration (query param / localStorage / user data must not be read on
 * the server - it would break SSG/ISR caching).
 */
export const usePolicySummary = (): void => {
  const { setPolicySummary } = usePageDataContext();
  const { brand } = useClientContext();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) {
      return;
    }
    ranRef.current = true;

    const applicationId = getApplicationIdFromUrl();
    const snapshot = readPurchaseSnapshot(applicationId);

    // No valid local snapshot -> invalid immediately; skip the network call.
    if (!applicationId || !snapshot) {
      setPolicySummary({ status: 'invalid', isPending: false });
      return;
    }

    setPolicySummary({
      status: 'loading',
      isPending: false,
      firstName: snapshot.firstName,
    });

    feApiFrontClient
      .getPolicySummary({ applicationId })
      .then((data) => {
        const state = resolvePolicySummaryState(applicationId, snapshot, data);
        setPolicySummary(state);

        // Purchase confirmed -> fire the GA `purchase_status` event once
        // (deduped per application id inside firePurchaseStatusEvent).
        const market =
          brand === 'singsaver' || brand === 'moneyhero' ? brand : undefined;
        if (state.status === 'ready' && state.data && market) {
          void firePurchaseStatusEvent({
            applicationId,
            snapshot,
            data: state.data,
            market,
          });
        }
      })
      .catch(() => setPolicySummary({ status: 'invalid', isPending: false }));
  }, [setPolicySummary, brand]);
};
